import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, IconButton, Link } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import LinkIcon from '@mui/icons-material/Link';
import { css } from '@emotion/react';
import { useAtomValue } from 'jotai';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { MUIAvatar } from '../../../../components/Avatar/MUIAvatar';
import { FormFactoryNoLayout } from '../../../../components/Form/FormFactory';
import { ROUTES } from '../../../../constants/RouteNameMapping';
import { ICompanyDataModel } from '../../../../data-models/company.data-model';
import { IDealDataModel } from '../../../../data-models/deal.data-model';
import { PermissionKey } from '../../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../../services/PermissionService';
import { companyStateJ } from '../../../../services/state/CompanyStateJ';
import { DealBoardDealCategory } from '../../../../types';
import { FDMap } from '../../../../util/data-structure/FDMap';
import { schemaToFormFields } from '../../../../util/schema-utils';
import { formatExternalLink, toURLFriendlyStringWithDashes } from '../../../../util/url-utils';
import { ICompanyViewModel } from '../../../../view-models/company.view-model';
import { createForm } from '../../../../view-models/form.view-model';
import { useDebounceCompanyUpdates } from '../../../CompanyProfiles2/Summary/useDebounceCompanyUpdates';
import { companyFormSchema } from '../../../Finance2/Forms/CompanyFormUtils';
import { dealStageIdToCategoryState } from '../../state/DealboardDataState';
import { getDealCategory } from '../../utils/getDealCategory';
import { CheckListWarningIcon } from '../CheckList/CheckList2';
import { colors } from '../../../../theme/colors';
import { ActiveDealMoreActionsButton, PassDealButton } from './ActiveDealActionsButton';
import { DrawerNavigation } from './DrawerNavigation';
import { NextStageButton } from './NextStageButton';
import { PassedDealMenu } from './PassSplitButton';
import { PriorityStar } from './PriorityStar';
import { TrackedDealMenu } from './TrackSplitButton';
import { DealStatusBox } from './DealStatusBox';

const Wrapper = css`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  width: 100%;
  justify-content: space-between;
  margin-top: 0.2rem;
`;

const ButtonWrapper = css`
  justify-self: end;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const DealInfoWrapper = css`
  display: flex;
  flex-direction: column;
  backdrop-filter: blur(20px);
  background: rgba(255, 255, 255, 0.4);
`;

const CompanyNameAndLogoWrapper = css`
  display: grid;
  grid-template-columns: max-content 1fr repeat(4, auto);
  @supports (field-sizing: content) {
    grid-template-columns: max-content repeat(5, auto);
  }

  align-items: center;
  justify-content: flex-start;
  margin-bottom: 8px;
  & * {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  & img {
    height: 27px;
    max-width: 5rem;
    object-fit: contain;
  }
  & input {
    @supports (field-sizing: content) {
      field-sizing: content;
    }
  }
  & .MuiFormControl-root {
    margin-left: 0.2rem;
    margin-top: 0.5rem;
  }
  &&&&& label {
    display: none;
  }
  &&&&& .MuiInputBase-input {
    font-size: 1.4rem;
  }
`;

const alertDuration = 2000;

export interface IDrawerHeaderProps {
  selectedDeal: IDealDataModel;
  company: ICompanyDataModel;
}

export const DrawerHeader: FC<IDrawerHeaderProps> = (props) => {
  const { selectedDeal } = props;
  const dealStageIdToCategory = useAtomValue(dealStageIdToCategoryState);
  const [showLoader, setShowLoader] = useState(false);
  const [alert, setAlert] = useState<{ message: string; severity: 'success' | 'error' }>();
  const [showAlert, setShowAlert] = useState(false);
  useEffect(() => {
    if (!alert) {
      return;
    }
    setShowAlert(true);
    const timerId = setTimeout(() => {
      setShowAlert(false);
      setAlert(undefined);
    }, alertDuration);
    return () => clearTimeout(timerId);
  }, [alert]);

  const dealCategory = getDealCategory(dealStageIdToCategory, selectedDeal.stageId);

  return (
    <div>
      {showLoader && <LinearProgress style={{ marginBottom: '2.5rem' }} />}
      {showAlert && (
        <Alert severity={alert?.severity} style={{ position: 'absolute' }}>
          {alert?.message}
        </Alert>
      )}
      {!showLoader && <DrawerNavigation />}
      <div css={DealInfoWrapper}>
        <div css={Wrapper}>
          <CompanyNameField selectedDeal={selectedDeal} />
          {PermissionService.get().hasPermission(PermissionKey.canEditDeal) && (
            <div css={ButtonWrapper}>
              {dealCategory === DealBoardDealCategory.TRACK && <TrackedDealMenu />}
              {dealCategory === DealBoardDealCategory.PASS && <PassedDealMenu />}
              {dealCategory === DealBoardDealCategory.CURRENT && (
                <>
                  <PassDealButton />
                  <NextStageButton setShowLoader={setShowLoader} setAlert={setAlert} />
                  <ActiveDealMoreActionsButton />
                </>
              )}
            </div>
          )}
        </div>
        <CompanyDescriptionField companyId={selectedDeal.companyId} />

        <DealStatusBox deal={selectedDeal} />
      </div>
    </div>
  );
};

function CompanyNameField({ selectedDeal }: { selectedDeal: IDealDataModel }) {
  const updateCompany = useDebounceCompanyUpdates(selectedDeal.companyId);
  const schema = companyFormSchema().pick(['name']);
  const company = useAtomValue(companyStateJ(selectedDeal.companyId));

  const methods = useForm<Pick<ICompanyViewModel, 'name'>>({
    defaultValues: {
      name: company?.name,
    },
    mode: 'onBlur',
  });

  const onUpdateCompany = useCallback(
    async (data: Partial<ICompanyViewModel>) => {
      if (data.name) {
        updateCompany({ name: data.name });
      }
    },
    [updateCompany]
  );

  const companyNameForm = useMemo(() => {
    const [field] = schemaToFormFields(schema);

    return createForm<ICompanyViewModel>({
      fields: FDMap.fromArray([{ ...field, label: '', disabled: company?.crunchbaseId != null }], 'key'),
      variant: 'form-inline',
    });
  }, [company?.crunchbaseId, schema]);

  if (!company) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <div css={CompanyNameAndLogoWrapper}>
        <MUIAvatar size='mediumLarge' src={company.logoUrl ?? ''} nameParts={company.name.split(' ')} />
        <FormFactoryNoLayout form={companyNameForm} onChange={onUpdateCompany} />
        <IconButton
          component={Link}
          href={`/${ROUTES.COMPANY_PROFILES}/${toURLFriendlyStringWithDashes(company.name)}-${
            selectedDeal.companyId
          }/summary`}
          target='_blank'
          rel='noreferrer'
        >
          <OpenInNewIcon color='secondary' fontSize='small' />
        </IconButton>
        {company.website ? (
          <IconButton href={formatExternalLink(company.website ?? '')} target='_blank'>
            <LinkIcon htmlColor={colors.neutral[60]} fontSize='small' />
          </IconButton>
        ) : null}
        <CheckListWarningIcon deal={selectedDeal} />
        <PriorityStar deal={selectedDeal} />
      </div>
    </FormProvider>
  );
}

export function CompanyDescriptionField({ companyId }: { companyId: number }) {
  const updateCompany = useDebounceCompanyUpdates(companyId);
  const schema = companyFormSchema().pick(['shortDescription']);
  const company = useAtomValue(companyStateJ(companyId));

  const methods = useForm<Pick<ICompanyViewModel, 'shortDescription'>>({
    defaultValues: {
      shortDescription: company?.shortDescription,
    },
  });

  const onUpdateCompany = useCallback(
    async (data: Partial<ICompanyViewModel>) => {
      if (data.shortDescription) {
        updateCompany({ shortDescription: data.shortDescription });
      }
    },
    [updateCompany]
  );

  const companyDescriptionForm = useMemo(() => {
    const [field] = schemaToFormFields(schema);

    return createForm<ICompanyViewModel>({
      fields: FDMap.fromArray([{ ...field, label: '', rendererMeta: { multiline: true } }], 'key'),
      variant: 'form-inline',
    });
  }, [schema]);

  if (!company) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <FormFactoryNoLayout form={companyDescriptionForm} onChange={onUpdateCompany} />
    </FormProvider>
  );
}
